import React, { useEffect } from "react"
import { Form, Input, Col, DatePicker } from "antd"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { CustomSelect, PhoneInput } from "ui/Input"
import { handleLastNameBlur, handleLastNameInput, handleLastNameKeyPress } from "pagesV2/pro/utils/ProUtils"
import { genderOptions } from "utils/gender"
import PropTypes from "prop-types"

const ImportErrorForm = ({ form, rowData, errorFields, errorMessages = {} }) => {
  const { t } = useTranslation()
  const handleNameKeyDown = (e) => {
    const key = e.key
    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"]

    if (!/^[A-Za-z]$/.test(key) && !allowedKeys.includes(key)) {
      e.preventDefault()
    }
  }
  const onInputChange = (value) => {
    // PhoneInput returns { countryCode, fullNumber }, extract fullNumber for validation
    const phoneNumber = value?.fullNumber || value
    form.setFieldsValue({ phone_number: phoneNumber })
  }
  const handleDateKeyDown = (e) => {
    if (e.key.length === 1 && !/\d/.test(e.key) && e.key !== "/" && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
    }
  }

  const fieldConfigs = {
    first_name: {
      render: () => (
        <Col xs={24} sm={12}>
          <Form.Item
            name="first_name"
            label={t("client.first_name")}
            rules={[{ required: true, message: t("client.error_messages.first_name_required") }]}
          >
            <Input onKeyDown={handleNameKeyDown} placeholder={t("client.first_name")} />
          </Form.Item>
        </Col>
      )
    },
    last_name: {
      render: () => (
        <Col xs={24} sm={12}>
          <Form.Item
            name="last_name"
            label={t("client.last_name")}
            rules={[{ required: true, message: t("client.error_messages.last_name_required") }]}
          >
            <Input
              onKeyDown={handleLastNameKeyPress}
              onInput={handleLastNameInput}
              placeholder={t("client.last_name")}
              onBlur={(e) => handleLastNameBlur(e, form)}
            />
          </Form.Item>
        </Col>
      )
    },
    email: {
      render: () => (
        <Col xs={24} sm={12}>
          <Form.Item
            name="email"
            label={t("client.email")}
            rules={[
              { required: true, message: t("client.error_messages.email_required") },
              { type: "email", message: t("client.error_messages.email_invalid") }
            ]}
          >
            <Input placeholder={t("client.enter_email")} />
          </Form.Item>
        </Col>
      )
    },
    birthday: {
      render: () => (
        <Col xs={24} sm={12}>
          <Form.Item name="birthday" label={t("client.birthday")}>
            <DatePicker
              style={{ width: "100%", height: "44px" }}
              placeholder="mm/dd/yyyy"
              format="MM/DD/YYYY"
              disabledDate={(current) => current && current > Date.now()}
              onKeyDown={handleDateKeyDown}
            />
          </Form.Item>
        </Col>
      )
    }
  }

  // Set form errors from errorMessages
  useEffect(() => {
    if (errorMessages && Object.keys(errorMessages).length > 0) {
      const fields = Object.entries(errorMessages).map(([name, message]) => ({
        name: name === "country_code" ? "phone_number" : name,
        errors: [typeof message === "string" ? message : "Invalid value"]
      }))
      form.setFields(fields)
    }
  }, [errorMessages, form])

  const uniqueErrorFields = [...new Set(errorFields?.map((field) => (field === "country_code" ? "phone_number" : field)))]

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        ...rowData,
        birthday: rowData.birthday ? dayjs(rowData.birthday) : null
      }}
    >
      <div className="row">
        {uniqueErrorFields?.map((field) => {
          const config = fieldConfigs[field]
          return config ? <React.Fragment key={field}>{config.render()}</React.Fragment> : null
        })}
      </div>
    </Form>
  )
}
ImportErrorForm.propTypes = {
  form: PropTypes.shape({
    setFieldsValue: PropTypes.func.isRequired,
    setFields: PropTypes.func.isRequired
    // ...other form methods if needed
  }).isRequired,
  rowData: PropTypes.shape({
    phone_number: PropTypes.object,
    birthday: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
      PropTypes.object // for dayjs or moment objects
    ])
    // ...add other rowData fields as needed
  }),
  errorFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  errorMessages: PropTypes.object
}

export default ImportErrorForm
